import { map } from "lodash";
import {
  CorporaPlatform,
  CorporaSocialSource,
  CorporaTotalSourcesByPlatform,
  CorporaWebSource,
} from "@linkfluence/models/corpora";
import { Platform, isWebPlatform } from "@linkfluence/models/platform";
import { CorporaSource, CorporaSourcePayload } from "@radarly/models/corpora";
import { RadarlyAPI } from "./base";

const reverseFormatInstagramSource = (source: CorporaSocialSource) =>
  source.screenName;

const reverseFormatTwitterSource = (source: CorporaSocialSource) => {
  // Remove '@' before calling API
  const screenName = source.screenName
    ? source.screenName.indexOf("@") === 0
      ? source.screenName.substring(1)
      : source.screenName
    : "";
  return {
    name: source.label,
    screenName,
    userId: "" + source.id,
  };
};

const reverseFormatYoutubeSource = (source: CorporaSocialSource) => ({
  name: source.label,
  screenName: source.screenName,
  userId: "" + source.id,
});

const reverseFormatTwitchSource = (source: CorporaSocialSource) =>
  source.screenName || source.label;

const reverseFormatPinterestSource = (source: CorporaSocialSource) =>
  source.id || source.label;

const reverseFormatSinaweiboSource = (source: CorporaSocialSource) => ({
  name: source.label,
  screenName: source.screenName,
  userId: "" + source.id,
});

const reverseFormatFacebookSource = (source: CorporaSocialSource) => ({
  name: source.label,
  pageId: "" + source.id,
});

const reverseFormatCritizrSource = (source: CorporaSocialSource) => ({
  id: source.id,
  name: source.label,
  credentialId: source.credentialId,
});

const reverseFormatWebSource = (source: CorporaWebSource) =>
  source.url || source.label;

const reverseFormatSource = (source, platform) => {
  if (platform === "web" || isWebPlatform(platform)) {
    return reverseFormatWebSource(source);
  } else {
    const fetcher: Record<Exclude<CorporaPlatform, "web">, any> = {
      [CorporaPlatform.Twitter]: reverseFormatTwitterSource,
      [CorporaPlatform.Facebook]: reverseFormatFacebookSource,
      [CorporaPlatform.Instagram]: reverseFormatInstagramSource,
      [CorporaPlatform.Youtube]: reverseFormatYoutubeSource,
      [CorporaPlatform.Twitch]: reverseFormatTwitchSource,
      [CorporaPlatform.Review]: reverseFormatWebSource,
      [CorporaPlatform.Feed]: reverseFormatWebSource,
      [CorporaPlatform.Sinaweibo]: reverseFormatSinaweiboSource,
      [CorporaPlatform.Critizr]: reverseFormatCritizrSource,
      [CorporaPlatform.Pinterest]: reverseFormatPinterestSource,
    };
    return fetcher[platform](source);
  }
};

const getBackendFormatedSources = (
  sources: CorporaSource[],
  platform: CorporaPlatform
) => {
  return map(sources, (source) => reverseFormatSource(source, platform));
};

const formatPlatform = (platform): string =>
  platform === "web" ? "website" : platform;

function mapSourceIds(sources, platform) {
  const idName =
    platform === "web" ||
    isWebPlatform(platform) ||
    platform === CorporaPlatform.Review
      ? "label"
      : platform === "instagram"
      ? "screenName"
      : "id";
  return map(sources, idName);
}

export class CorporaAPI extends RadarlyAPI {
  public async getSountSourcesOnCorpus(project: number, corpus: number) {
    return this.get<{ sourcesCount: number }>(
      `/projects/${project}/corpora/${corpus}/sourcesCount.json`
    );
  }
  public async setSourcesOnCorpus(
    project: number,
    corpus: number,
    sources: CorporaSource[],
    platform: CorporaPlatform
  ) {
    return this.post(
      `/projects/${project}/corpora/${corpus}/sources.json?source=${formatPlatform(
        platform
      )}`,
      {
        sources: getBackendFormatedSources(sources, platform),
      }
    );
  }
  public async checkSourcesOnCorpus(
    project: number,
    corpus: number,
    sources: CorporaSource[],
    platform: CorporaPlatform
  ) {
    const sourceIds = mapSourceIds(sources, platform);
    return this.post(
      `/projects/${project}/corpora/${corpus}/check/sources.json?source=${formatPlatform(
        platform
      )}`,
      {
        sources: sourceIds,
      }
    );
  }

  public async getCorporaTotalSourcesByPlatform(project: number) {
    return await this.get<CorporaTotalSourcesByPlatform>(
      `/projects/${project}/corporaDistribution.json`
    );
  }

  public async getCorporaPlatformSources(
    project: number,
    corpusId: number,
    platform: Platform,
    payload: CorporaSourcePayload
  ) {
    return await this.post<CorporaSource[]>(
      `/projects/${project}/corpora/${corpusId}.json?source=${platform}`,
      payload
    );
  }
}
